import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { type FeedResponse, type CreatePostDto, type UpdatePostDto, type AddReactionDto, type CreateCommentDto } from '../../services/pulse/pulse.types';
import { client } from '../client';

const makeOptimisticPostItem = (
  postId: string,
  content: string,
  authorId: string,
  visibility: string = 'workspace',
  organizationId: string
) => ({
  id: postId,
  organizationId,
  type: 'POST' as const,
  sourceModule: 'PULSE',
  sourceEntityType: 'PULSE_POST',
  sourceEntityId: postId,
  sourceAction: 'POST_CREATED',
  visibility,
  actorId: authorId,
  authorName: 'Voc',
  authorRole: 'Gestor do Workspace',
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  content,
  reactions: {} as any,
  replies: [],
  reactionsData: [],
});

export function usePulseFeed(params: {
  organizationId?: string;
  cursor?: string;
  limit?: number;
  sourceModule?: string;
  search?: string;
} = {}) {
  // organizationId is derived securely from the JWT by the API and is not a
  // permitted query parameter (the global ValidationPipe rejects unknown keys).
  const { organizationId: _organizationId, ...queryParams } = params;
  return useQuery<FeedResponse>({
    queryKey: ['pulse', 'feed', params.organizationId, params],
    queryFn: () =>
      client.get('/pulse/feed', { params: queryParams }).then(r => r.data),
    enabled: !!params.organizationId,
    staleTime: 30_000,
    retry: 1,
  });
}

export function useCreatePulsePost() {
  const queryClient = useQueryClient();
  return useMutation<any, Error, any, any>({
    mutationFn: ({ organizationId, userId, dto }: { organizationId: string; userId: string; dto: CreatePostDto }) =>
      client.post('/pulse/posts', { organizationId, userId, ...dto }),
    onMutate: async (newPost) => {
      await queryClient.cancelQueries({ queryKey: ['pulse', 'feed'] });
      const previousFeed = queryClient.getQueryData<FeedResponse>([
        'pulse', 'feed', newPost.organizationId,
      ]);
      const optimisticItem = makeOptimisticPostItem(
        `optimistic-${Date.now()}`,
        newPost.dto.content,
        newPost.userId,
        newPost.dto.visibility || 'workspace',
        newPost.organizationId
      );
      queryClient.setQueryData<FeedResponse>(['pulse', 'feed', newPost.organizationId], (old) =>
        old
          ? { items: [optimisticItem, ...(old.items || [])], nextCursor: old.nextCursor, hasMore: old.hasMore }
          : old
      );
      return { previousFeed };
    },
    onError: (_err, newPost, ctx) => {
      if (ctx?.previousFeed) {
        queryClient.setQueryData<FeedResponse>(['pulse', 'feed', newPost.organizationId], ctx.previousFeed);
      }
    },
    onSettled: (_, __, newPost) => {
      queryClient.invalidateQueries({ queryKey: ['pulse', 'feed', newPost?.organizationId] });
    },
  });
}

export function useUpdatePulsePost() {
  const queryClient = useQueryClient();
  return useMutation<any, Error, any, any>({
    mutationFn: ({ organizationId, postId, dto }: { organizationId: string; postId: string; dto: UpdatePostDto }) =>
      client.put(`/pulse/posts/${postId}`, { organizationId, dto }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pulse', 'feed'] });
    },
  });
}

export function useDeletePulsePost() {
  const queryClient = useQueryClient();
  return useMutation<any, Error, any, any>({
    mutationFn: ({ organizationId, postId }: { organizationId: string; postId: string }) =>
      client.delete(`/pulse/posts/${postId}`, { params: { organizationId } }),
    onSuccess: (_result, vars) => {
      queryClient.setQueriesData<FeedResponse>({ queryKey: ['pulse', 'feed', vars.organizationId] }, (old) => {
        if (!old) return old;
        return {
          ...old,
          items: old.items.filter((item) => item.id !== vars.postId && item.sourceEntityId !== vars.postId),
        };
      });
      queryClient.invalidateQueries({ queryKey: ['pulse', 'feed', vars.organizationId] });
    },
  });
}

export function useAddPulseReaction() {
  const queryClient = useQueryClient();
  return useMutation<any, Error, any, any>({
    mutationFn: ({ organizationId, postId, dto }: { organizationId: string; postId: string; dto: AddReactionDto }) =>
      client.post(`/pulse/posts/${postId}/reactions`, { organizationId, ...dto }),
    onMutate: async ({ organizationId, postId, dto }) => {
      await queryClient.cancelQueries({ queryKey: ['pulse', 'feed', organizationId] });
      const previousFeed = queryClient.getQueryData<FeedResponse>(['pulse', 'feed', organizationId]);
      queryClient.setQueryData<FeedResponse>(['pulse', 'feed', organizationId], (old) => {
        if (!old) return old;
        return {
          ...old,
          items: (old.items || []).map((item) => {
            if (item.id !== postId && item.sourceEntityId !== postId) return item;
            const reactionKey = dto.type;
            const currentReactions = { ...item.reactions };
            const userReactions = [...(currentReactions.userReactions || [])];
            for (const previousType of userReactions.filter((v) => v !== reactionKey)) {
              if (typeof currentReactions[previousType] === 'number') {
                currentReactions[previousType] = Math.max(0, (currentReactions[previousType] as number) - 1);
              }
            }
            if (!userReactions.includes(reactionKey)) userReactions.push(reactionKey);
            currentReactions[reactionKey] = (typeof currentReactions[reactionKey] === 'number' ? currentReactions[reactionKey] : 0) + 1;
            return { ...item, reactions: { ...currentReactions, userReactions: [reactionKey] }, reactionsData: item.reactionsData };
          }),
        };
      });
      return { previousFeed };
    },
    onError: (_err, vars, ctx) => {
      if (ctx?.previousFeed) {
        queryClient.setQueryData<FeedResponse>(['pulse', 'feed', vars.organizationId], ctx.previousFeed);
      }
    },
    onSettled: (_, __, vars) => {
      queryClient.invalidateQueries({ queryKey: ['pulse', 'feed', vars?.organizationId] });
    },
  });
}

export function useRemovePulseReaction() {
  const queryClient = useQueryClient();
  return useMutation<any, Error, any, any>({
    mutationFn: ({ organizationId, postId, dto }: { organizationId: string; postId: string; dto: AddReactionDto }) =>
      client.delete(`/pulse/posts/${postId}/reactions`, { data: { organizationId, ...dto } }),
    onMutate: async ({ organizationId, postId, dto }) => {
      await queryClient.cancelQueries({ queryKey: ['pulse', 'feed', organizationId] });
      const previousFeed = queryClient.getQueryData<FeedResponse>(['pulse', 'feed', organizationId]);
      queryClient.setQueryData<FeedResponse>(['pulse', 'feed', organizationId], (old) => {
        if (!old) return old;
        return {
          ...old,
          items: (old.items || []).map((item) => {
            if (item.id !== postId && item.sourceEntityId !== postId) return item;
            const reactionKey = dto.type;
            const currentReactions = { ...item.reactions };
            const userReactions = (currentReactions.userReactions || []).filter((r) => r !== reactionKey);
            const currentCount = typeof currentReactions[reactionKey] === 'number' ? currentReactions[reactionKey] as number : 0;
            currentReactions[reactionKey] = Math.max(0, currentCount - 1);
            if (currentReactions[reactionKey] === 0) delete currentReactions[reactionKey];
            return {
              ...item,
              reactions: { ...currentReactions, userReactions: userReactions.length > 0 ? userReactions : undefined },
              reactionsData: item.reactionsData,
            };
          }),
        };
      });
      return { previousFeed };
    },
    onError: (_err, vars, ctx) => {
      if (ctx?.previousFeed) {
        queryClient.setQueryData<FeedResponse>(['pulse', 'feed', vars.organizationId], ctx.previousFeed);
      }
    },
    onSettled: (_, __, vars) => {
      queryClient.invalidateQueries({ queryKey: ['pulse', 'feed', vars?.organizationId] });
    },
  });
}

export function useGetPulseComments(postId: string | number | undefined) {
  return useQuery<FeedResponse>({
    queryKey: ['pulse', 'comments', postId],
    queryFn: () => client.get(`/pulse/posts/${postId}/comments`).then(r => r.data),
    enabled: !!postId,
    staleTime: 30_000,
  });
}

export function useAddPulseComment() {
  const queryClient = useQueryClient();
  return useMutation<any, Error, any, any>({
    mutationFn: ({ postId, dto }: { postId: string | number; dto: CreateCommentDto }) =>
      client.post(`/pulse/posts/${postId}/comments`, dto),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['pulse', 'comments', variables.postId] });
      queryClient.invalidateQueries({ queryKey: ['pulse', 'feed'] });
    },
  });
}


